import {useEffect, useState} from "react";
import {IssueApiJson} from "../IssueApiJson";
import AjaxService from "../../../platform/ajax/AjaxService";
import AuthenticationService from "../../../platform/authentication/AuthenticationService";


export const HOT_ISSUE_STORAGE_KEY = "currentHotIssueId";
const CROSS_TAB_POLL_INTERVAL_MS = 2000;

function isAuthenticatedNow(): boolean {
    const status = AuthenticationService.getCurrentAuthStatus() as unknown as {
        authenticated: boolean;
        authenticationStatusKnown: boolean;
    };
    return status.authenticated === true && status.authenticationStatusKnown === true;
}

export type HotIssueSummary = {
    id: string;
    name: string;
    creationTimestampIso: string;
    originalCreatorName: string;
}

export function useHotIssue():
    {
        currentHotIssueSummary: null | HotIssueSummary;
        clearHotIssue: () => void;
        makeHotIssue: (issueId: string) => void;
    } {

    const [currentHotIssueId, setCurrentHotIssueId] = useState<string | null>(null);
    const [loadedHodIssue, setLoadedHotIssue] = useState<IssueApiJson | null>(null);

    useEffect(() => {
        const syncFromStorage = () => {
            if (!isAuthenticatedNow()) {
                setCurrentHotIssueId(prev => prev == null ? prev : null);
                return;
            }
            const storedHotIssue = localStorage.getItem(HOT_ISSUE_STORAGE_KEY);
            setCurrentHotIssueId(prev => prev === storedHotIssue ? prev : storedHotIssue);
        };
        syncFromStorage();
        const intervalId = window.setInterval(syncFromStorage, CROSS_TAB_POLL_INTERVAL_MS);
        AuthenticationService.addAuthStatusChangeListener(syncFromStorage);
        return () => window.clearInterval(intervalId);
    }, []);

    const clearHotIssue = () => {
        localStorage.removeItem(HOT_ISSUE_STORAGE_KEY);
        setCurrentHotIssueId(null);
    }

    const makeHotIssue = (issueId: string) => {
        localStorage.setItem(HOT_ISSUE_STORAGE_KEY, issueId);
        setCurrentHotIssueId(issueId);
    }

    useEffect(() => {
        if (currentHotIssueId == null) {
            setLoadedHotIssue(null);
            return;
        }
        if (!isAuthenticatedNow()) {
            return;
        }
        AjaxService.getJson(`/api/issue/${currentHotIssueId}?origin=hot`)
            .then((obj) => {
                setLoadedHotIssue(obj.json as IssueApiJson);
                console.log("Loaded hot issue", obj.json);
            })
            .catch((err) => {
                // Swallow locally to prevent the global unhandled-rejection handler from triggering
                // a full-page reload loop when the access token has expired; clear stored id so the
                // 2s cross-tab interval stops re-firing the same failing request.
                console.warn("Hot issue background fetch failed; clearing stored hot issue id", err);
                localStorage.removeItem(HOT_ISSUE_STORAGE_KEY);
                setCurrentHotIssueId(null);
                setLoadedHotIssue(null);
            });
    }, [currentHotIssueId]);

    const currentHotIssueSummary: HotIssueSummary | null = loadedHodIssue == null ? null : {
        id: loadedHodIssue.id,
        name: loadedHodIssue.name,
        creationTimestampIso: loadedHodIssue.creationInstant,
        originalCreatorName: loadedHodIssue.originalCreatorName
    }

    return {
        currentHotIssueSummary,
        clearHotIssue,
        makeHotIssue,
    }
}